import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/fiat-millecento-detail.webp";
import HeroImgMobile       from "../../assets/images/blogs/fiat-millecento-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";
import InlineImg1          from "../../assets/images/blogs/fiat-millecento-inline-1.webp";
import InlineImg2          from "../../assets/images/blogs/fiat-millecento-inline-1.webp";
import InlineImg3          from "../../assets/images/blogs/fiat-millecento-inline-1.webp";
// import InlineImg4          from "../../assets/images/blogs/fiat-millecento-inline-1.webp";

/* ─────────────────────────────────────────────
   WorldsFirstElectrifiedVintage1954FiatMillecento.jsx
   Prefix      : wf-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   DIFF vs other pages:
   - Hero title  : CENTERED
   - wf-quote    : italic opening quote (no date on this page)
   - wf-inline-img-wrap : left-aligned ~530px inline image (x4)
   - wf-link     : inline purple hyperlink
   - No subheads — all wf-para
   - Newsletter  : prop-based isMobile, Subscribe button
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .wf-wrap *, .wf-wrap *::before, .wf-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .wf-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .wf-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .wf-content-col { width: 100%; }

  /* ── SCROLL FADE-UP ── */
  .wf-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .wf-fade-up.wf-in { opacity: 1; transform: translateY(0); }
  .wf-delay-1 { transition-delay: 0.08s; }
  .wf-delay-2 { transition-delay: 0.18s; }
  .wf-delay-3 { transition-delay: 0.28s; }
  .wf-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO — centered title
  ══════════════════════════════════════ */
  .wf-hero { width: 100%; background: #1A1A1A; }
  .wf-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .wf-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .wf-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .wf-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .wf-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* italic opening quote */
  .wf-quote {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  .wf-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* italic para — last para on image 4 */
  .wf-para--italic {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* inline hyperlink */
  .wf-link {
    color: #743FAC;
    text-decoration: underline;
    word-break: break-all;
  }
  .wf-link:hover {
    color: #9b59f5;
  }

  /* inline content image — left aligned ~530px */
  .wf-inline-img-wrap {
    margin: 44px 0;
    width: 530px;
    max-width: 100%;
  }
  .wf-inline-img-wrap img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .wf-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .wf-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
   
    object-position: center;
    z-index: 0;
  }
  .wf-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .wf-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .wf-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .wf-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .wf-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .wf-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .wf-newsletter__input::placeholder { color: #FFFFFF; }
  .wf-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .wf-newsletter__btn:hover { background: #5e2e90; }
  .wf-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .wf-newsletter__btn:hover .wf-newsletter__btn-arrow { transform: translateX(4px); }
  .wf-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .wf-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .wf-inner { padding-left: 32px; padding-right: 32px; }
    .wf-hero__img-wrap img { max-height: 350px; }
    .wf-hero__title-bar { padding: 24px 0px 20px; }
    .wf-hero__title { font-size: 24px; }
    .wf-body-section { padding: 40px 0 48px; }
    .wf-para { margin-bottom: 18px; font-size: 15px; }
    .wf-para--italic { margin-bottom: 18px; font-size: 15px; }
    .wf-quote { font-size: 15px; margin-bottom: 18px; }
    .wf-inline-img-wrap { width: 100%; margin: 28px 0; }

    .wf-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .wf-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .wf-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .wf-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .wf-newsletter__heading { font-size: 24px; }
    .wf-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .wf-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .wf-newsletter__input { border-radius: 5px; height: 44px; }
    .wf-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .wf-inner { padding-left: 16px; padding-right: 16px; }
    .wf-hero__title-bar { padding: 30px 0px; }
    .wf-body-section { padding: 30px 0 30px; }
    .wf-hero__img-wrap img { max-height: 366px; width: 100%; }
    .wf-hero__title { font-size: 26px; line-height: 120%; }
    .wf-para { font-size: 14px; margin-bottom: 14px; }
    .wf-para--italic { font-size: 14px; margin-bottom: 14px; }
    .wf-quote { font-size: 14px; margin-bottom: 14px; }
    .wf-inline-img-wrap { width: 100%; margin: 30px 0; }

    .wf-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .wf-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .wf-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .wf-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .wf-newsletter__heading { font-size: 30px; line-height: 120%; }
    .wf-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .wf-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .wf-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .wf-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".wf-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("wf-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="wf-newsletter">
      {!isMobile && (
        <>
          <img className="wf-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="wf-newsletter__overlay" />
        </>
      )}
      <div className="wf-newsletter__inner">
        {isMobile && (
          <img className="wf-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="wf-newsletter__content">
          <h2 className="wf-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="wf-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="wf-newsletter__form">
            <input
              className="wf-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="wf-newsletter__btn">
              Subscribe
              <span className="wf-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function WorldsFirstElectrifiedVintage1954FiatMillecento() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="wf-wrap">

        {/* HERO — centered title */}
        <section className="wf-hero">
          <div className="wf-hero__title-bar">
            <div className="wf-inner">
              <h1 className="wf-hero__title">World's First Electrified Vintage 1954 Fiat Millecento</h1>
            </div>
          </div>
          <div className="wf-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="1954 Fiat Millecento EV"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="wf-body-section">
          <div className="wf-inner">
            <div className="wf-content-col">

              {/* italic opening quote — image 1 */}
              <p className="wf-quote wf-fade-up wf-delay-1">"Using old bottles to store new wines. Rides from the past that can now go green."</p>

              <p className="wf-para wf-fade-up wf-delay-1">When the great lockdowns of 2020 came upon us... there was not much to do. So much stress about business, our customers, the delay in the launch of our new DC fast charger product line due to the collapse in supply chains. Our daily check-in meetings were full of people sharing their worries about our business and their personal lives. We decided to do something about it... and keep ourselves motivated and focused. And so we decided to take a wonderfully restored 1954 Fiat Millecento and convert this babe from an internal combustion engine vehicle to a fully battery-electric vehicle. We reasoned that we got it right, this car could be used on our charging products and could be a wonderful mascot at auto shows where our DC Quench fast chargers will be displayed.</p>

              <p className="wf-para wf-fade-up wf-delay-1">This particular model was imported into India for a period after independence and then a slightly larger version of the same shape was manufactured for some time as well as a precursor to the Premier President and Premier Padmini's, which lasted to the 1990s. It's a wonderful design full of romance and cuteness, with doors opening unconventionally in front providing wide access space to very cozy interiors.</p>

              {/* inline image 1 — image 2 */}
              <div className="wf-inline-img-wrap wf-fade-up wf-delay-2">
                <img src={InlineImg1} alt="1954 Fiat Millecento at charging station" />
              </div>

              <p className="wf-para wf-fade-up wf-delay-1">The car we worked on was a labor of love two times over. It was first discovered abandoned on the side of a highway, rusty and not in a roadworthy condition by a very unique and interesting Frenchman and baker living in Pune — Brice POISSON. Brice found the car, negotiated to buy it, and then had it lovingly restored with fidelity to its factory finish of 1954 — even having the car repainted in the same colors as cars that were delivered in 1954. Even the interiors were redone with much detail and fidelity to the original factory finishes. A Premier Padmini engine from the 1970s was installed under the hood. Brice soon decided to move back to Paris and we were lucky to be the ones to him Brice sold his labor of love.</p>

              <p className="wf-para wf-fade-up wf-delay-1">The lockdowns gave our project the impetus it needed and our entire team worked in batches to convert this car with such a unique history into a vehicle of the future. One team worked on the dismantling of the Internal combustion engine. Another worked on the undercarriage to make it ready for the electric motor. Yet another team worked on rehabilitating the paintwork &amp; chrome and another worked on stripping the petrol fuel tanks and lines. With not much to do, most of it got done in a matter of days. Funny how a little bit of focus makes so much difference.</p>

              {/* inline image 2 — image 3 */}
              <div className="wf-inline-img-wrap wf-fade-up wf-delay-2">
                <img src={InlineImg2} alt="1954 Fiat Millecento conversion process" />
              </div>

              <p className="wf-para wf-fade-up wf-delay-1">Then the final team working with our electric motor vendor worked on installing the Lithium-ion battery pack in the boot and the powerful electric motor in the front. It was quite the moment when we did our first test drive and this grand old lady moved forward without making a sound. There were giggles and whoopies everywhere.</p>

              <p className="wf-para wf-fade-up wf-delay-1">We soon discovered that the electric motor was far too powerful for the brake design and so had to detune the motor whilst we searched for disc brakes we could modify at a milling shop to have installed. That took a few weeks of hunting the bazaars and auto markets. Scamsters and chor bazaar rogues were the order of the day to negotiate with. But determined we were — so dealing with them was par for the course.</p>

              <p className="wf-para wf-fade-up wf-delay-1">Finally — with the right brakes, we could work the electric motor back to full torque and enjoy the feeling of being pushed back into your seat as this silent angel makes off down the road.</p>

              <p className="wf-para wf-fade-up wf-delay-1">There is something beautiful about seeing a vintage vehicle glide down the road silently. So much history, one can feel the spirit of the team that conceptualized the design of the vehicle in the 1940s, the team that manufactured it in the 1950s, and the people who had maintained and serviced it since. There is so much meaning stored in such a small piece of metal. Sadly, these cars aren't allowed on public roads any longer since they comply with the safety and emissions rules of the Fifties and Sixties. But our little Millecento is different. The conversion of classic cars to electric is probably a method to revive the use of these wonderful vehicles and keep them alive.</p>

              {/* inline image 3 — image 4 first */}
              <div className="wf-inline-img-wrap wf-fade-up wf-delay-2">
                <img src={InlineImg3} alt="1954 Fiat Millecento EV ready" />
              </div>

              <p className="wf-para wf-fade-up wf-delay-1">We called our 1954 Fiat Millecento EV or more likely our labor of love — the E-Diggi and it is now our proud mascot. Customers love to experience a test drive whenever they visit our campus and on special days everyone gets to try their hands on the wheel for a spin. E Diggi is the prime attraction at auto shows and any visitors to our office.</p>

              <p className="wf-para wf-fade-up wf-delay-1">Our first core value is "Fight every inch" and our team certainly did that to resurrect this beautiful work of automotive art and make it a vehicle of the future.</p>

              
              {/* italic para with inline link — image 4 */}
              <p className="wf-para--italic wf-fade-up wf-delay-1">
                Listen to our podcast with key industry leaders on how we can accelerate the adoption of EVs in India:&nbsp;
                <a
                  href="https://energizingindia.tv/episodes/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="wf-link"
                >
                  https://energizingindia.tv/episodes/
                </a>
              </p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}